import { isPlatformBrowser } from '@angular/common';
import { contentChildren, Directive, effect, ElementRef, inject, PLATFORM_ID } from '@angular/core';

/**
 * Marks a single text block that should be revealed on scroll. Apply it to each block inside
 * an `[appScrollReveal]` container. The `scroll-reveal-item` class (see the global stylesheet)
 * blends the block from a dimmed resting color toward the focused color as `--reveal` rises.
 */
@Directive({
  selector: '[appScrollRevealItem]',
  host: { class: 'scroll-reveal-item' },
})
export class ScrollRevealItem {
  readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
}

/**
 * Drives the scroll-reveal text animation for every `[appScrollRevealItem]` nested inside the
 * host element. Each block is revealed as its top edge rises past a focal line and kept revealed
 * once it scrolls above; scrolling back down lowers the edge and reverses the reveal.
 *
 * A single rAF-coalesced scroll/resize loop updates the `--reveal` (0 → 1) custom property on
 * every block, so adding more blocks never adds more listeners.
 */
@Directive({
  selector: '[appScrollReveal]',
})
export class ScrollReveal {
  private readonly items = contentChildren(ScrollRevealItem, { descendants: true });

  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      const items = this.items();
      let frameId = 0;

      const render = (): void => {
        frameId = 0;

        const focalLine = window.innerHeight * 0.5;
        const falloff = window.innerHeight * 0.25;

        items.forEach(({ element }) => {
          const { top } = element.nativeElement.getBoundingClientRect();

          // Above the focal line stays fully revealed; below it fades in as it approaches.
          const reveal = top <= focalLine ? 1 : Math.max(0, 1 - (top - focalLine) / falloff);
          element.nativeElement.style.setProperty('--reveal', reveal.toFixed(3));
        });
      };

      // Coalesce bursts of scroll/resize events into a single update per frame.
      const schedule = (): void => {
        frameId ||= requestAnimationFrame(render);
      };

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      render();

      onCleanup(() => {
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        cancelAnimationFrame(frameId);
      });
    });
  }
}
